import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Modal, Checkbox, Button, Space, Select, InputNumber, Input, Popover, message } from 'antd';
import {
  MenuOutlined,
  LeftOutlined,
  RightOutlined,
  PlusOutlined,
  MinusOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import type { ColumnSettingItem, ColumnSettingValue } from './types';

import './ColumnSettingModal.css';

const WIDTH_MIN = 15;
const WIDTH_STEP = 10;
const SIZE_OPTIONS = [
  { value: 'small', label: '紧凑' },
  { value: 'middle', label: '默认' },
  { value: 'large', label: '宽松' },
] as const;

export interface ColumnSettingModalProps {
  open: boolean;
  /** 弹框定位：表头左下角 (x,y)，不传则居中 */
  position?: { x: number; y: number };
  /** 序号列是否显示 */
  rowIndexVisible: boolean;
  /** 数据列配置（不含序号列、操作列） */
  columnItems: ColumnSettingItem[];
  /** 默认配置（用于重置） */
  defaultRowIndexVisible: boolean;
  defaultColumnItems: ColumnSettingItem[];
  /** 自动滚动（scroll.x）：true=横向滚动，false=max-content */
  scrollX?: boolean;
  defaultScrollX?: boolean;
  /** 表格 size */
  size?: 'small' | 'middle' | 'large';
  defaultSize?: 'small' | 'middle' | 'large';
  onSave: (value: ColumnSettingValue) => void;
  onClose: () => void;
  /** 是否显示「保存」按钮（持久化到服务端） */
  persistEnabled?: boolean;
  /** 点击「保存」时调用；成功/失败由上层或本组件提示 */
  onPersist?: (value: ColumnSettingValue) => void | Promise<void>;
}

/** 列设置弹窗宽度（与 AndTable 定位计算保持一致） */
export const AND_TABLE_COLUMN_SETTING_MODAL_WIDTH = 560;

/** 从 items + scrollX + size 生成 ColumnSettingValue */
export function toColumnSettingValue(
  rowIndexVisible: boolean,
  items: ColumnSettingItem[],
  scrollX?: boolean,
  size?: 'small' | 'middle' | 'large'
): ColumnSettingValue {
  const widthMap: Record<string, number | 'auto'> = {};
  items.forEach((i) => {
    if (i.width !== undefined) widthMap[i.key] = i.width;
  });
  return {
    rowIndexVisible,
    order: items.map((i) => i.key),
    visible: items.reduce<Record<string, boolean>>((acc, i) => {
      acc[i.key] = i.visible;
      return acc;
    }, {}),
    fixed: items.reduce<Record<string, 'left' | 'right' | false>>((acc, i) => {
      if (i.fixed) acc[i.key] = i.fixed;
      return acc;
    }, {}),
    width: Object.keys(widthMap).length ? widthMap : undefined,
    scrollX,
    size,
  };
}

/** 从 ColumnSettingValue 恢复 items 顺序和配置，缺省用 defaultItems */
export function applyColumnSettingValue(
  value: ColumnSettingValue | undefined,
  defaultItems: ColumnSettingItem[],
  defaultRowIndexVisible: boolean,
  defaultSize?: 'small' | 'middle' | 'large'
): {
  rowIndexVisible: boolean;
  items: ColumnSettingItem[];
  /** store 未写入时为 undefined，由上层按 props scroll-x / defaultScrollX 回退 */
  scrollX?: boolean;
  size: 'small' | 'middle' | 'large';
} {
  const order = value?.order ?? defaultItems.map((i) => i.key);
  const visibleMap = value?.visible ?? {};
  const fixedMap = value?.fixed ?? {};
  const widthMap = value?.width ?? {};
  const keyToDefault = new Map(defaultItems.map((i) => [i.key, i]));

  const items: ColumnSettingItem[] = [];
  const seen = new Set<string>();

  for (const key of order) {
    const def = keyToDefault.get(key);
    if (!def) continue;
    seen.add(key);
    items.push({
      key: def.key,
      title: def.title,
      visible: visibleMap[key] ?? def.visible,
      fixed: fixedMap[key] ?? def.fixed,
      width: widthMap[key] ?? def.width,
    });
  }
  for (const def of defaultItems) {
    if (!seen.has(def.key)) {
      items.push({
        ...def,
        visible: visibleMap[def.key] ?? def.visible,
        fixed: fixedMap[def.key] ?? def.fixed,
        width: widthMap[def.key] ?? def.width,
      });
    }
  }

  return {
    rowIndexVisible: value?.rowIndexVisible ?? defaultRowIndexVisible,
    items,
    scrollX: value?.scrollX,
    size: value?.size ?? defaultSize ?? 'middle',
  };
}

const MODAL_WIDTH = AND_TABLE_COLUMN_SETTING_MODAL_WIDTH;
const MODAL_MAX_HEIGHT = 440;

export const ColumnSettingModal: React.FC<ColumnSettingModalProps> = ({
  open,
  position,
  rowIndexVisible,
  columnItems,
  defaultRowIndexVisible,
  defaultColumnItems,
  scrollX: scrollXProp = false,
  defaultScrollX = false,
  size: sizeProp = 'middle',
  defaultSize = 'middle',
  onSave,
  onClose,
  persistEnabled = false,
  onPersist,
}) => {
  const [rowIndexVisibleLocal, setRowIndexVisibleLocal] = useState(rowIndexVisible);
  const [items, setItems] = useState<ColumnSettingItem[]>(columnItems);
  const [scrollXLocal, setScrollXLocal] = useState(scrollXProp);
  const [sizeLocal, setSizeLocal] = useState<'small' | 'middle' | 'large'>(sizeProp);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [persisting, setPersisting] = useState(false);
  const [columnFilterKeyword, setColumnFilterKeyword] = useState('');
  const [filterVisibleOnly, setFilterVisibleOnly] = useState(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const columnFilterInputRef = useRef<any>(null);

  useEffect(() => {
    if (open) {
      setRowIndexVisibleLocal(rowIndexVisible);
      setItems([...columnItems]);
      setScrollXLocal(scrollXProp);
      setSizeLocal(sizeProp);
      setColumnFilterKeyword('');
      setFilterVisibleOnly(false);
      setFilterPopoverOpen(false);
    }
  }, [open, rowIndexVisible, columnItems, scrollXProp, sizeProp]);

  const filterActive = useMemo(
    () => columnFilterKeyword.trim() !== '' || filterVisibleOnly,
    [columnFilterKeyword, filterVisibleOnly],
  );

  const filteredItems = useMemo(() => {
    let result = items;
    const keyword = columnFilterKeyword.trim().toLowerCase();
    if (keyword) {
      result = result.filter(
        (col) =>
          col.title.toLowerCase().includes(keyword) || col.key.toLowerCase().includes(keyword),
      );
    }
    if (filterVisibleOnly) {
      result = result.filter((col) => col.visible);
    }
    return result;
  }, [items, columnFilterKeyword, filterVisibleOnly]);

  const clearColumnFilter = useCallback(() => {
    setColumnFilterKeyword('');
    setFilterVisibleOnly(false);
  }, []);

  const columnFilterPanel = (
    <div className="and-table-column-setting-filter-panel">
      <Input
        ref={columnFilterInputRef}
        size="small"
        allowClear
        placeholder="搜索列名或字段"
        prefix={<SearchOutlined />}
        value={columnFilterKeyword}
        onChange={(e) => setColumnFilterKeyword(e.target.value)}
      />
      <Checkbox
        checked={filterVisibleOnly}
        onChange={(e) => setFilterVisibleOnly(e.target.checked)}
      >
        仅显示已勾选列
      </Checkbox>
      {filterActive ? (
        <Button type="link" size="small" className="and-table-column-setting-filter-reset" onClick={clearColumnFilter}>
          重置筛选
        </Button>
      ) : null}
    </div>
  );

  const flushSave = useCallback(
    (value: ColumnSettingValue) => {
      queueMicrotask(() => onSave(value));
    },
    [onSave]
  );

  const buildValue = useCallback(
    (rowIdx: boolean, list: ColumnSettingItem[], scroll?: boolean, sz?: 'small' | 'middle' | 'large') =>
      toColumnSettingValue(rowIdx, list, scroll ?? scrollXLocal, sz ?? sizeLocal),
    [scrollXLocal, sizeLocal]
  );

  const handleReset = useCallback(() => {
    setRowIndexVisibleLocal(defaultRowIndexVisible);
    const resetItems = defaultColumnItems.map((i) => ({ ...i }));
    setItems(resetItems);
    setScrollXLocal(defaultScrollX);
    setSizeLocal(defaultSize);
    flushSave(toColumnSettingValue(defaultRowIndexVisible, resetItems, defaultScrollX, defaultSize));
  }, [defaultRowIndexVisible, defaultColumnItems, defaultScrollX, defaultSize, flushSave]);

  const toggleVisible = useCallback(
    (key: string) => {
      setItems((prev) => {
        const next = prev.map((i) => (i.key === key ? { ...i, visible: !i.visible } : i));
        flushSave(buildValue(rowIndexVisibleLocal, next));
        return next;
      });
    },
    [rowIndexVisibleLocal, buildValue, flushSave]
  );

  const setFixed = useCallback(
    (key: string, fixed: 'left' | 'right' | false) => {
      setItems((prev) => {
        const next = prev.map((i) => (i.key === key ? { ...i, fixed } : i));
        flushSave(buildValue(rowIndexVisibleLocal, next));
        return next;
      });
    },
    [rowIndexVisibleLocal, buildValue, flushSave]
  );

  const setWidth = useCallback(
    (key: string, width: number | 'auto') => {
      setItems((prev) => {
        const next = prev.map((i) => (i.key === key ? { ...i, width } : i));
        flushSave(buildValue(rowIndexVisibleLocal, next));
        return next;
      });
    },
    [rowIndexVisibleLocal, buildValue, flushSave]
  );

  const adjustWidth = useCallback(
    (key: string, delta: number) => {
      setItems((prev) => {
        const item = prev.find((i) => i.key === key);
        if (!item) return prev;
        const current = typeof item.width === 'number' ? item.width : 80;
        const nextVal = Math.max(WIDTH_MIN, current + delta);
        const next = prev.map((i) => (i.key === key ? { ...i, width: nextVal } : i));
        flushSave(buildValue(rowIndexVisibleLocal, next));
        return next;
      });
    },
    [rowIndexVisibleLocal, buildValue, flushSave]
  );

  const moveItem = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (toIndex < 0 || toIndex >= items.length) return;
      const next = [...items];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      setItems(next);
      flushSave(buildValue(rowIndexVisibleLocal, next));
    },
    [items, rowIndexVisibleLocal, buildValue, flushSave]
  );

  const handleRowIndexVisibleChange = useCallback(
    (checked: boolean) => {
      setRowIndexVisibleLocal(checked);
      flushSave(buildValue(checked, items));
    },
    [items, buildValue, flushSave]
  );

  const handleScrollXChange = useCallback(
    (checked: boolean) => {
      setScrollXLocal(checked);
      flushSave(buildValue(rowIndexVisibleLocal, items, checked, sizeLocal));
    },
    [rowIndexVisibleLocal, items, sizeLocal, buildValue, flushSave]
  );

  const handleSizeChange = useCallback(
    (val: 'small' | 'middle' | 'large') => {
      setSizeLocal(val);
      flushSave(buildValue(rowIndexVisibleLocal, items, scrollXLocal, val));
    },
    [rowIndexVisibleLocal, items, scrollXLocal, buildValue, flushSave]
  );

  const onDragStart = (index: number) => (e: React.DragEvent) => {
    if (filterActive) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };
  const onDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (filterActive) return;
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex === null || draggedIndex === index) return;
    moveItem(draggedIndex, index);
    setDraggedIndex(index);
  };
  const onDragEnd = () => setDraggedIndex(null);

  const handlePersist = useCallback(async () => {
    if (!onPersist) {
      message.error('未配置列设置保存接口');
      return;
    }
    const value = buildValue(rowIndexVisibleLocal, items);
    setPersisting(true);
    try {
      await onPersist(value);
    } catch (error) {
      console.error('[AndTable] persist column settings failed', error);
      message.error('列设置保存失败');
    } finally {
      setPersisting(false);
    }
  }, [onPersist, buildValue, rowIndexVisibleLocal, items]);

  const modalStyle =
    position != null
      ? (() => {
          let top = position.y;
          let left = position.x;
          if (typeof window !== 'undefined') {
            if (left + MODAL_WIDTH > window.innerWidth) left = window.innerWidth - MODAL_WIDTH - 16;
            if (left < 16) left = 16;
            if (top + MODAL_MAX_HEIGHT > window.innerHeight) top = Math.max(16, window.innerHeight - MODAL_MAX_HEIGHT - 16);
            if (top < 16) top = 16;
          }
          return { margin: 0, top, left };
        })()
      : undefined;

  return (
    <Modal
      title="列设置"
      open={open}
      onCancel={onClose}
      footer={null}
      width={MODAL_WIDTH}
      destroyOnHidden
      centered={position == null}
      style={modalStyle}
      mask={false}
      className={`and-table-column-setting-modal${position != null ? ' and-table-column-setting-modal-positioned' : ''}`}
    >
      <div className="and-table-column-setting-body">
        <div className="and-table-column-setting-header">
          <div className="and-table-column-setting-header-row">
            <Checkbox
              checked={rowIndexVisibleLocal}
              onChange={(e) => handleRowIndexVisibleChange(e.target.checked)}
            >
              序号列
            </Checkbox>
            <Checkbox checked={scrollXLocal} onChange={(e) => handleScrollXChange(e.target.checked)}>
              自动滚动
            </Checkbox>
            <Space align="center">
              <span className="and-table-column-setting-label">表格</span>
              <Select
                size="small"
                value={sizeLocal}
                onChange={handleSizeChange}
                options={[...SIZE_OPTIONS]}
                style={{ width: 80 }}
              />
            </Space>
          </div>
        </div>

        <div className="and-table-column-setting-list-wrap">
          <table className="and-table-column-setting-table">
            <thead>
              <tr>
                <th className="and-table-column-setting-th-drag">排序</th>
                <th className="and-table-column-setting-th-name">
                  <div className="and-table-column-setting-th-name-inner">
                    <span className="and-table-column-setting-th-name-label">列名/可见</span>
                    <Popover
                      trigger="click"
                      placement="bottomRight"
                      open={filterPopoverOpen}
                      onOpenChange={(nextOpen) => {
                        setFilterPopoverOpen(nextOpen);
                        if (nextOpen) {
                          queueMicrotask(() => columnFilterInputRef.current?.focus?.());
                        }
                      }}
                      content={columnFilterPanel}
                      overlayClassName="and-table-column-setting-filter-popover"
                    >
                      <button
                        type="button"
                        className={`and-table-column-setting-filter-trigger${filterActive ? ' active' : ''}`}
                        title={filterActive ? '已启用筛选' : '筛选列'}
                        aria-label="筛选列"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FilterOutlined />
                      </button>
                    </Popover>
                  </div>
                </th>
                <th className="and-table-column-setting-th-width">宽度(px)</th>
                <th className="and-table-column-setting-th-fix">固定</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="and-table-column-setting-empty">
                    无匹配列
                  </td>
                </tr>
              ) : (
                filteredItems.map((col) => {
                  const index = items.findIndex((i) => i.key === col.key);
                  if (index < 0) return null;
                  const widthNum = typeof col.width === 'number' ? col.width : null;
                  const isAuto = col.width === 'auto' || (col.width === undefined && widthNum === null);
                  return (
                    <tr
                      key={col.key}
                      className={`and-table-column-setting-row${filterActive ? ' and-table-column-setting-row-filtered' : ''} ${draggedIndex === index ? 'and-table-column-setting-row-dragging' : ''}`}
                      draggable={!filterActive}
                      onDragStart={onDragStart(index)}
                      onDragOver={onDragOver(index)}
                      onDragEnd={onDragEnd}
                    >
                    <td className="and-table-column-setting-td-drag">
                      <span
                        className="and-table-column-setting-drag"
                        title={filterActive ? '清空筛选后可排序' : '拖动排序'}
                      >
                        <MenuOutlined />
                      </span>
                    </td>
                    <td className="and-table-column-setting-td-name">
                      <Checkbox checked={col.visible} onChange={() => toggleVisible(col.key)} />
                      <span className="and-table-column-setting-title" title={col.title}>
                        {col.title}
                      </span>
                    </td>
                    <td className="and-table-column-setting-td-width">
                      <div className="and-table-column-setting-width">
                        <span
                          className="and-table-column-setting-fix-btn"
                          onClick={() => adjustWidth(col.key, -WIDTH_STEP)}
                          title={`减 ${WIDTH_STEP}px`}
                        >
                          <MinusOutlined />
                        </span>
                        <InputNumber
                          size="small"
                          min={WIDTH_MIN}
                          step={WIDTH_STEP}
                          value={isAuto ? undefined : (widthNum ?? 80)}
                          placeholder="auto"
                          controls={false}
                          style={{ width: 52 }}
                          onChange={(v) => {
                            if (v == null || (typeof v === 'string' && v === '')) setWidth(col.key, 'auto');
                            else setWidth(col.key, Math.max(WIDTH_MIN, Number(v)));
                          }}
                        />
                        <span
                          className="and-table-column-setting-fix-btn"
                          onClick={() => adjustWidth(col.key, WIDTH_STEP)}
                          title={`加 ${WIDTH_STEP}px`}
                        >
                          <PlusOutlined />
                        </span>
                      </div>
                    </td>
                    <td className="and-table-column-setting-td-fix">
                      <Space className="and-table-column-setting-fix">
                        <span
                          className={`and-table-column-setting-fix-btn ${col.fixed === 'left' ? 'active' : ''}`}
                          onClick={() => setFixed(col.key, col.fixed === 'left' ? false : 'left')}
                          title="固定到左侧"
                        >
                          <LeftOutlined />
                        </span>
                        <span className="and-table-column-setting-fix-divider">|</span>
                        <span
                          className={`and-table-column-setting-fix-btn ${col.fixed === 'right' ? 'active' : ''}`}
                          onClick={() => setFixed(col.key, col.fixed === 'right' ? false : 'right')}
                          title="固定到右侧"
                        >
                          <RightOutlined />
                        </span>
                      </Space>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="and-table-column-setting-footer">
          {persistEnabled ? (
            <Button type="primary" loading={persisting} onClick={() => void handlePersist()}>
              保存
            </Button>
          ) : null}
          <Button onClick={handleReset}>重置</Button>
        </div>
      </div>
    </Modal>
  );
};
